"use client";

import { useEffect } from "react";
import supabase from "@/lib/supabase/client";
import { useUserStore } from "@/lib/store/userStore";
import { logoutTracker } from "./logoutTracker";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

export default function AuthStateListener() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const setProfile = useUserStore((s) => s.setProfile);
  const clearProfile = useUserStore((s) => s.clear);
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Refresh client-side user data when session becomes available/updates
      if (
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED" ||
        (event === "INITIAL_SESSION" && !!session)
      ) {
        // Invalidate user-related queries so components refetch with the new session
        queryClient.invalidateQueries({
          queryKey: ["userProfile"],
          exact: false,
        });
        // Hydrate user store with latest profile (users table)
        (async () => {
          try {
            const { data: userData } = await supabase.auth.getUser();
            const user = userData?.user;
            if (!user) return;
            const { data, error } = await supabase
              .from("users")
              .select("email, tier, team_id, role, velia")
              .eq("id", user.id)
              .single();
            if (!error && data) {
              setProfile({
                id: user.id,
                email: (data as any).email ?? user.email ?? "",
                tier: (data as any).tier ?? "BASIC",
                team_id: (data as any).team_id ?? null,
                role: (data as any).role ?? null,
                velia: (data as any).velia ?? null,
              } as any);
            }
          } catch (e) {
            // noop
          }
        })();
      }

      if (event === "SIGNED_OUT") {
        const url =
          typeof window !== "undefined" ? new URL(window.location.href) : null;
        const isPasswordChangeFlow =
          url?.searchParams.get("passwordChanged") === "1";
        const atRoot = url?.pathname === "/";

        // Always navigate to login on SIGNED_OUT to avoid stale screen
        if (logoutTracker.isManual) {
          logoutTracker.setManualLogout(false);
          if (!atRoot) {
            // Hard navigation to clear SPA/bfcache
            window.location.replace("/");
          }
          setTimeout(() => queryClient.clear(), 100);
          return;
        }

        if (!isPasswordChangeFlow) {
          toast.error(
            "세션이 만료되었거나 다른 곳에서 로그인하여 자동 로그아웃되었습니다.",
            { id: "auth-signed-out" },
          );
        }
        if (!atRoot) {
          window.location.replace("/");
        }
        setTimeout(() => queryClient.clear(), 100);
        clearProfile();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return null;
}
